import { gridOf, hexagonRadius, type BoardDef } from '@archon3d/core';

export interface Point {
  x: number;
  y: number;
}

/** Where cells sit on the editor's 2D canvas, in drawing units (`size` per tile). */
export interface CanvasLayout {
  width: number;
  height: number;
  centre(x: number, y: number): Point;
  /** Corners of the cell's outline, clockwise. */
  outline(x: number, y: number): Point[];
  /** Ends of the edge two adjacent cells share, where a wall is drawn. */
  edge(a: Point, b: Point): [Point, Point];
}

const ROW_PITCH = Math.sqrt(3) / 2;

/**
 * Squares side by side, or pointy-top hexes in "odd-r" rows: odd rows shifted half a tile east,
 * rows √3/2 of a tile apart – the same layout `cellToWorld` uses on the table.
 */
export function canvasLayout(
  def: Pick<BoardDef, 'grid' | 'width' | 'height'>,
  size = 40,
): CanvasLayout {
  const grid = gridOf({ width: def.width, height: def.height, def });
  if (grid === 'square') {
    const centre = (x: number, y: number) => ({ x: (x + 0.5) * size, y: (y + 0.5) * size });
    return {
      width: def.width * size,
      height: def.height * size,
      centre,
      outline: (x, y) => [
        { x: x * size, y: y * size },
        { x: (x + 1) * size, y: y * size },
        { x: (x + 1) * size, y: (y + 1) * size },
        { x: x * size, y: (y + 1) * size },
      ],
      edge: (a, b) => sharedEdge(centre(a.x, a.y), centre(b.x, b.y), size),
    };
  }
  // Circumradius of a hex whose flat-to-flat width is one tile; it is also the edge length.
  const radius = size / Math.sqrt(3);
  const centre = (x: number, y: number) => ({
    x: (x + 0.5 + (y & 1) * 0.5) * size,
    y: radius + y * ROW_PITCH * size,
  });
  return {
    width: (def.width + (def.height > 1 ? 0.5 : 0)) * size,
    height: 2 * radius + (def.height - 1) * ROW_PITCH * size,
    centre,
    outline: (x, y) => {
      const c = centre(x, y);
      return Array.from({ length: 6 }, (_, i) => {
        const angle = (Math.PI / 180) * (60 * i - 30);
        return { x: c.x + radius * Math.cos(angle), y: c.y + radius * Math.sin(angle) };
      });
    },
    edge: (a, b) => sharedEdge(centre(a.x, a.y), centre(b.x, b.y), radius),
  };
}

/** The segment of length `length` through the midpoint of two centres, at right angles to them. */
function sharedEdge(a: Point, b: Point, length: number): [Point, Point] {
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = Math.hypot(dx, dy) || 1;
  const px = (-dy / d) * (length / 2);
  const py = (dx / d) * (length / 2);
  return [
    { x: mid.x - px, y: mid.y - py },
    { x: mid.x + px, y: mid.y + py },
  ];
}

/**
 * The hexagon a hexagon-shaped hex board outlines, as the table frames it: a flat-top hexagon
 * round the centre cell whose sides touch the rim hexes' outer corners (inradius
 * radius·√3/2 + 1/√3 tiles). Its own corners reach a sixth of a tile past the outermost hexes'
 * flat sides. `undefined` for squares and rectangles of hexes.
 */
export function hexagonOutline(
  def: Pick<BoardDef, 'grid' | 'width' | 'height' | 'cells'>,
  size = 40,
): Point[] | undefined {
  const radius = hexagonRadius(def);
  if (radius === undefined) return undefined;
  const c = canvasLayout(def, size).centre(radius, radius);
  const circumradius = ((radius * ROW_PITCH + 1 / Math.sqrt(3)) * size) / Math.cos(Math.PI / 6);
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 3) * i;
    return { x: c.x + circumradius * Math.cos(angle), y: c.y + circumradius * Math.sin(angle) };
  });
}

export function pointsAttribute(points: Point[]): string {
  return points.map((p) => `${round(p.x)},${round(p.y)}`).join(' ');
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
